import { Controller } from '@hotwired/stimulus';
import { Calendar } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import NavigationHelperController from "./navigation_helper_controller";
import CalendarEventsManagementService from "./calendar_events/events_management_service";
import { parseDate, longFormatDate, calculateWeeksBetween } from "../utils/dates";

const MOBILE_MAX_WIDTH = 768;
const DEFAULT_MONTH_NUMBER = 5;
const DEFAULT_DAY_NUMBER = 1;
const COLORS = ['bg-orange-600', 'bg-pink-700', 'bg-green-600', 'bg-teal-500', 'bg-red-600', 'bg-orange-700', 'bg-blue-700', 'bg-pink-850', 'bg-purple-800'];
const DEFAULT_COLOR = 'bg-blue-400';

export default class extends Controller {
  static targets = ['calendar', 'daysNavigationContainer', 'summerPopup'];
  static values = { excludedAssignables: { type: String, default: '' } }
  kids = [];
  eventColor = {};

  async connect() {
    await this.#getUserKids();
    this.#setKidsColors();
    this.#setUpWelcomePopup();
    this.calendarEventManagement = new CalendarEventsManagementService(this.excludedAssignablesValue ? this.excludedAssignablesValue.split(',') : []);

    this.calendar = new Calendar(this.calendarTarget, {
      plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin],
      initialView: window.innerWidth < MOBILE_MAX_WIDTH ? 'timeGridDay' : 'dayGridMonth',
      initialDate: this.#getInitialDate(),
      events: this.#fetchCalendarEvents.bind(this),
      dayHeaderFormat: {
        weekday: 'long'
      },
      headerToolbar: {
        start: 'prevMonth,prev',
        center: 'title',
        end: 'next,nextMonth'
      },
      eventContent: this.#renderEventContent.bind(this),
      windowResize: () => { 
        const newView = window.innerWidth < 768 ? 'timeGridDay' : 'dayGridMonth';
        if (this.calendar.view.type !== newView) {
          this.calendar.changeView(newView);
        }
      },
      customButtons: {
        prevMonth: {
          text: 'PreviousMonth',
          click: () => { this.#changeMonth(-1); },
          icon: 'chevrons-left'
        },
        nextMonth: {
          text: 'Next month',
          click: () => { this.#changeMonth(1); },
          icon: 'chevrons-right'
        }
      },
      slotLabelFormat: {
        hour: '2-digit',
        minute: '2-digit',
        meridiem: 'narrow',
        hour12: false
      },
      datesSet: this.#updateCustomDateButtons.bind(this),
      eventTimeFormat: {
        hour: 'numeric',
        minute: '2-digit',
        meridiem: true
      },
      displayEventEnd: true,
      eventClick: this.#handleEventClick
    });

    this.calendar.on('datesSet', () => this.calendarEventManagement.syncCalendarDates(this.calendar));
    this.calendar.render();
  }

  disconnect() {
    this.calendar.destroy();
  }

  #setKidsColors() {
    this.kids.forEach((kid, index) => {
      this.eventColor[kid.id] = COLORS[index % COLORS.length];
    });
  }

  #getInitialDate() {
    const urlParams = new URLSearchParams(window.location.search);
    const startDateFromParams = urlParams.get('start_date');
    const defaultDate = new Date(new Date().getFullYear(), DEFAULT_MONTH_NUMBER, DEFAULT_DAY_NUMBER);

    return parseDate(startDateFromParams) || defaultDate;
  }

  #handleEventClick(info) {
    const isEditable = info.event.extendedProps.editable;
    if (isEditable) {
      Turbo.visit(`/calendar_events/${info.event.id}/edit`, { frame: 'new-event-form' });
    }
  }

  #renderEventContent(info) {
    const eventTitle = info.event.title;
    const isSchoolEvent = info.event.extendedProps?.context_type === 'School';

    if (isSchoolEvent) {
      return { html: `<div class="fc-event-title">${eventTitle}</div>` }
    };

    return {
      html: `
        <div class="inline-block whitespace-normal">
          <span class="fc-icon inline"><i class="fa-solid fa-user"></i></span>
          ${info.event.extendedProps?.assignable_name ? `<span class="fc-event-username inline">${info.event.extendedProps.assignable_name}</span>` : ''}
          ${info.timeText ? `<span class="fc-event-time inline">${info.timeText.toLowerCase()}: </span>` : ''}
          <span class="fc-event-title inline">${eventTitle}</span>
        </div>
      `
    };
  }

  /**
   * Custom day buttons for mobile day-grid calendar view
   */
  #updateCustomDateButtons(info) {
    const isDayView = info?.view?.type === 'timeGridDay';
    const currentDate = info?.view?.currentStart;
    if (!isDayView || !info?.view?.currentStart) return;

    let navigationsButtonContainer;

    if (this.hasDaysNavigationContainerTarget) {
      navigationsButtonContainer = this.daysNavigationContainerTarget;
      navigationsButtonContainer.innerHTML = '';
    } else {
      navigationsButtonContainer = document.createElement('div');
      navigationsButtonContainer.setAttribute('data-squad-planner-target', 'daysNavigationContainer');
      navigationsButtonContainer.classList.add('day-buttons-container');
    }
    
    let current = new Date(currentDate);
    
    for (let i = -3; i <= 3; i++) {
      const day = new Date(current);
      day.setDate(current.getDate() + i);
      
      const button = document.createElement('button');
      button.classList.add('day-button');

      const dayShortLabel = day.toLocaleDateString('en-US', { weekday: 'short' });
      button.innerHTML = `
        <span class="num">${day.getDate()}</span>
        <span class="day">${dayShortLabel}</span>
      `;
      
      if (i === 0) {
        button.classList.add('active');
      }
  
      button.addEventListener('click', function() {
        this.calendar.gotoDate(day);
      }.bind(this));
      
      navigationsButtonContainer.appendChild(button)
    }

    if (!this.hasDaysNavigationContainerTarget) {
      const dayGridContainer = this.calendarTarget.querySelector('.fc-view-harness.fc-view-harness-active');
      this.calendarTarget.insertBefore(navigationsButtonContainer, dayGridContainer);
    }
  }
  
  #changeMonth(offset) {
    const currentDate = this.calendar.getDate();
    const newDate = new Date(currentDate);
    newDate.setMonth(currentDate.getMonth() + offset);
    this.calendar.gotoDate(newDate);
  }

  async #fetchCalendarEvents(dateRange = {}) {
    const events = await this.calendarEventManagement.fetchEvents(dateRange);
    return this.#transformToFullCalendarEvents(events);
  }

  onAssignableSelected(e) {
    if (!this.calendarEventManagement) return [];
    const checkbox = e.target;
    this.calendarEventManagement.handleAssignableSelection(checkbox.value, checkbox.checked);

    this.calendar.refetchEvents();
  }

  async #getUserKids() {
    const csrfToken = document.querySelector("meta[name='csrf-token']").getAttribute('content');
    try {
      const response = await fetch('/user/visitors.json', {
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        }
      });
  
      const data = await response.json();
  
      this.kids = data || [];
    } catch (error) {
      console.log(error);
    }
  }

  refetchEvents (event) {
    if (event.detail.success) {
      this.calendar.refetchEvents();
    }
  }

  #setUpWelcomePopup() {
    if (!this.hasSummerPopupTarget) return;

    const schoolDateRage = this.#getSchoolDateRange(this.kids);
    if (!schoolDateRage) return;
    const { latestLastDay, earliestFirstDay, weeksBetween } = schoolDateRage;

    const placeholders = this.summerPopupTarget.querySelectorAll("[data-fill='true']");

    if (placeholders.length >= 3) {
      const [summerWeeks, summerStart, summerEnd] = placeholders;

      const [latestM, latestD] = latestLastDay.toUpperCase().split(' ');
      const [earliestM, earliestD] = earliestFirstDay.toUpperCase().split(' ');

      summerStart.innerHTML = `<span>${latestD}</span> <span>${latestM}</span>`;
      summerEnd.innerHTML = `<span>${earliestD}</span> <span>${earliestM}</span>`;
      summerWeeks.innerHTML = `${weeksBetween} weeks`;
    }

    this.summerPopupTarget.classList.remove('bottom-[-100%]');
    const navHelper = new NavigationHelperController();
    navHelper.removeParams();
  }

  /**
   * 
   * @param {*} kids kids data with their school dates
   * @returns and object with:
   * the earliest first_day_of_school,
   * the latest last_Day_of_school
   * and number of weeks in between
   */
  #getSchoolDateRange(kids) {
    if (!kids.length) return null;

    let earliestFirstDay = null;
    let latestLastDay = null;

    kids.forEach(kid => {
      const firstDay = new Date(kid.first_day_of_school);
      const lastDay = new Date(kid.last_day_of_school);

      if (kid.first_day_of_school && !isNaN(firstDay) && (!earliestFirstDay || firstDay < earliestFirstDay)) {
        earliestFirstDay = firstDay;
      }

      if (kid.last_day_of_school && !isNaN(lastDay) && (!latestLastDay || lastDay > latestLastDay)) {
        latestLastDay = lastDay;
      }
    });

    if (!latestLastDay || !earliestFirstDay) return null;

    const weeksBetween = calculateWeeksBetween(latestLastDay, earliestFirstDay);

    return {
      earliestFirstDay: longFormatDate(earliestFirstDay),
      latestLastDay: longFormatDate(latestLastDay),
      weeksBetween
    };
  }

  #isSchoolEvent(event) {
    const schoolEventsTitle = ['Last Day of School', 'First Day of School'];
    return event.context_type === 'School' && schoolEventsTitle.includes(event.title) && event.assignable?.first_name;
  }

  #eventParser(event, fixedProps = {}) {
    const eventIsPersonal = event.assignable_type === 'User';
    return {
      id: event.id,
      title: event.title,
      start: event.start_date,
      end: event.end_date,
      description: event.notes,
      allDay: event.start_date === event.end_date,
      classNames: this.eventColor[event.assignable.id] || DEFAULT_COLOR,
      ...fixedProps,
      extendedProps: {
        assignable_name: eventIsPersonal ? 'Personal' : event.assignable?.first_name,
        context_type: event.context_type,
        ...fixedProps.extendedProps
      }
    }
  }

  /**
   * @param {*} events data returned from the API
   * @returns events parsed into a format suitable for FullCalendar
   */
  #transformToFullCalendarEvents(events) {
    const isMonthView = this.calendar.view.type === 'dayGridMonth';
    const groupedSchoolEvents = new Map();
    const parsedEvents = [];

    events.forEach(event => {
      if (this.#isSchoolEvent(event)) {
        const key = `${event.title}-${event.start_date}`;
        const assignableName = event.assignable.first_name;
        
        if (!groupedSchoolEvents.has(key)) {
          groupedSchoolEvents.set(key, { 
            ...event, 
            assignables: [assignableName] 
          });
        } else {
          const groupedEvent = groupedSchoolEvents.get(key);
          if (!groupedEvent.assignables.includes(assignableName)) {
            groupedEvent.assignables.push(assignableName);
          }
        }
      } else {
        parsedEvents.push(this.#eventParser(event, { extendedProps: { editable: true } }));
      }
    });

    groupedSchoolEvents.forEach((event, key) => {
      const assignableNames = event.assignables.join(', ');
      parsedEvents.push(this.#eventParser(event, {
        id: key,
        title: `${assignableNames} - ${event.title}`,
        allDay: true,
        display: isMonthView ? 'background' : 'auto',
        classNames: ['fc-school-event']
      }));
    });

    return parsedEvents;
  }
}
